/*
 * A set of sagas to listen to changes in favorites
 */
import { spawn, call, select, take } from "redux-saga/effects";
import {
    FAVORITE_STYLE_SUCCESS,
    FETCH_FAVORITES_SUCCESS,
    UNFAVORITE_STYLE_SUCCESS
} from "../actions/favoritesActions.js";
import { debug } from '../utils/logger';
import { isFavoritesEnabled, favoriteTileStyle, unfavoriteTileStyle, updateGridFavorites } from '../utils/favoritesUtils';

/**
 * Updates the category grid and/or any product trays when favorites change
 *
 * @param styleFetcher Function that takes `state` and returns a list of style numbers in the current grid
 * @param styles Set of favorited style numbers
 */
export function *favoritesUpdatedSaga(styleFetcher, styles) {
    // Get all the style numbers from the grid
    const state = yield select();
    const styleNumbersInGrid = styleFetcher(state);

    updateGridFavorites(styleNumbersInGrid, styles);
}

/**
 * Creates a saga that listens for FETCH_FAVORITES_SUCCESS
 *
 * This action indicates that the favorites have been fetched successfully from the
 *  services.
 *
 */
export function createFavoritesUpdatedListenerSaga(styleFetcher) {
    return function(styleFetcher) {
        return function*() {
            debug("Saga: favoritesUpdateListenerSaga initiated");

            let action = {};
            while (action = yield take(FETCH_FAVORITES_SUCCESS)) {
                const styles = _.get(action, 'payload.styles', {});
                yield spawn(favoritesUpdatedSaga, styleFetcher, new Set(Object.keys(styles)));
            }
        };
    }(styleFetcher);
}

/**
 * Listens for FAVORITE_STYLE_SUCCESS.  A style was successfully favorited
 */
export function* favoritedListenerSaga() {
    debug("Saga: favoritedListenerSaga initiated");

    let action = {};
    while (action = yield take(FAVORITE_STYLE_SUCCESS)) {
        const styleNumber = _.get(action, 'payload.styleNumber', '');
        yield call(favoriteTileStyle, styleNumber);
    }
}

/**
 * Listens for UNFAVORITE_STYLE_SUCCESS.  A style was successfully unfavorited
 */
export function* unfavoritedListenerSaga() {
    debug("Saga: unfavoritedListenerSaga initiated");

    let action = {};
    while (action = yield take(UNFAVORITE_STYLE_SUCCESS)) {
        const styleNumber = _.get(action, 'payload.styleNumber', '');
        yield call(unfavoriteTileStyle, styleNumber);
    }
}

/**
 * Builds the listeners for a grid page or any page with product trays given a style fetcher
 * @param styleFetcher A function that takes `state` and returns an array of style numbers
 */
export function getGridFavoritesSagas(styleFetcher) {
    if (!isFavoritesEnabled()) {
        return [];
    }
    return [ createFavoritesUpdatedListenerSaga(styleFetcher), favoritedListenerSaga, unfavoritedListenerSaga ];
}
